import React, { useState, useEffect, useRef } from "react";
import { mealplanApi } from "../services/api";
import "./IngredientInput.css";

const STATUS_EMOJI = { enough: "🟢", running_low: "🟡", finished: "🔴" };

export default function IngredientInput({ onAdd, placeholder = "Add ingredient…" }) {
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selected, setSelected] = useState(null); // { id, name, status }
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setValue(val);
    setSelected(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.trim().length >= 1) {
      debounceRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const results = await mealplanApi.searchInventory(val.trim());
          setSuggestions(results);
          setShowSuggestions(true);
        } catch {
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelect = (item) => {
    setValue(item.name);
    setSelected(item);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleAdd = () => {
    if (!value.trim()) return;
    onAdd(value.trim(), selected?.id || null);
    setValue("");
    setSelected(null);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleAdd();
    if (e.key === "Escape") setShowSuggestions(false);
  };

  return (
    <div className="ing-input-wrap" ref={wrapRef}>
      <div className="ing-input-row">
        <div className="ing-input-field">
          <input
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder={placeholder}
            className="ing-text-input"
          />
          {selected && (
            <span className="ing-linked-badge">
              {STATUS_EMOJI[selected.status]} Linked
            </span>
          )}
          {showSuggestions && suggestions.length > 0 && (
            <div className="ing-suggestions">
              {suggestions.map((item) => (
                <div
                  key={item.id}
                  className="ing-suggestion-item"
                  onMouseDown={() => handleSelect(item)}
                >
                  <span className="ing-sug-emoji">{STATUS_EMOJI[item.status] || "⚪"}</span>
                  <span className="ing-sug-name">{item.name}</span>
                  {item.category && <span className="ing-sug-cat">{item.category}</span>}
                  <span className={`ing-sug-status ing-status-${item.status}`}>
                    {item.status === "enough" ? "Enough" : item.status === "running_low" ? "Low" : "Finished"}
                  </span>
                </div>
              ))}
              <div className="ing-sug-footer">
                {value.trim() && !suggestions.find(s => s.name.toLowerCase() === value.toLowerCase()) && (
                  <div className="ing-suggestion-item ing-new-item" onMouseDown={handleAdd}>
                    <span className="ing-sug-emoji">➕</span>
                    <span className="ing-sug-name">Add "<strong>{value}</strong>" as new</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <button className="ing-add-btn" onClick={handleAdd} disabled={!value.trim()}>
          + Add
        </button>
      </div>
    </div>
  );
}
